import { ApiError } from '@app/api/errors';
import { useUnitOptions } from '@app/ccf/company-report/utils';
import LifeCycleStageModelling from '@app/components/food-modelling/components/life-cycle-stage-modelling';
import { purchasedFoodModellingOptions } from '@app/components/food-modelling/food-modelling-options';
import { EmissionFactorsSchema } from '@app/components/food-modelling/schema';
import {
  emptyEmissionFactors,
  formatEmissionFactors,
  getEmissionFactors,
} from '@app/components/food-modelling/utils';
import useSimpleDialog from '@app/hooks/use-simple-dialog';
import { useItemScope } from '@app/purchased-goods/components/item-detail/use-item-scope';
import { purchasedGoodsRoutes } from '@app/purchased-goods/purchased-goods-routes';
import { useIntercom } from '@app/services/intercom/IntercomContext';
import { StageEnum } from '@app/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetActivityTypeUnits } from '@shared/api/hooks/ccf/units';
import {
  useDeletePurchasedFoodAndDrinkItem,
  usePatchPurchasedFoodAndDrinkItem,
} from '@shared/api/hooks/purchased-goods/food-and-drink';
import {
  FoodActivityRetrieveUpdate,
  PurchasedGoodModelledAsEnum,
} from '@shared/api/types';
import Heading from '@shared/components/content/heading';
import Text from '@shared/components/content/text';
import TextLink from '@shared/components/content/text-link';
import Unit from '@shared/components/content/unit';
import InputFormField from '@shared/components/form/input-form-field';
import SelectFormField from '@shared/components/form/select-form-field';
import IngredientPicker from '@shared/components/pickers/ingredient-picker';
import { emptyBaseFoodRow } from '@shared/components/product-form/components/recipe-base-food-row';
import RatingCloud from '@shared/components/ratings/rating-cloud';
import { Button } from '@shared/components/ui/button';
import { Form } from '@shared/components/ui/form';
import { useToast } from '@shared/components/ui/use-toast';
import { formatSmallNumber } from '@shared/lib/utils';
import sentry from '@shared/services/sentry';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { usePurchasedFoodAndDrinkItem } from '../../purchased-food-and-drink-item-context';
import ModelledFood from '../modelled-food';
import { purchasedItemFormSchema } from './purchased-food-and-drink-item-form-schema';

interface PurchasedFoodAndDrinkItemFormProps {
  item: FoodActivityRetrieveUpdate;
}

export default function PurchasedFoodAndDrinkItemForm({
  item,
}: PurchasedFoodAndDrinkItemFormProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { referrer, refetchItem } = usePurchasedFoodAndDrinkItem();
  const { scopeSearch } = useItemScope();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    SimpleDialogComponent: deleteModal,
    closeSimpleDialog: closeDeleteModal,
    showSimpleDialog: showDeleteModal,
  } = useSimpleDialog();
  const { showIntercomArticle } = useIntercom();

  const { mutateAsync: updateFoodActivity, isLoading: isUpdatingFoodActivity } =
    usePatchPurchasedFoodAndDrinkItem();

  const { mutateAsync: deleteFoodActivity, isLoading: isDeletingFoodActivity } =
    useDeletePurchasedFoodAndDrinkItem();

  const { data: units, isLoading: unitsIsLoading } =
    useGetActivityTypeUnits('food');
  const unitOptions = useUnitOptions(units!);

  const form = useForm<z.infer<typeof purchasedItemFormSchema>>({
    resolver: zodResolver(purchasedItemFormSchema),
  });

  const { reset, handleSubmit, watch } = form;

  const setEmissionFactors = useCallback(
    (
      modelledAs: PurchasedGoodModelledAsEnum,
      item: FoodActivityRetrieveUpdate,
      emissionFactors: any
    ) => {
      switch (modelledAs) {
        case PurchasedGoodModelledAsEnum.basic_food:
          return emissionFactors;
        case PurchasedGoodModelledAsEnum.modelled_food:
          return formatEmissionFactors(
            item.emissionsFactors as EmissionFactorsSchema
          );
        case PurchasedGoodModelledAsEnum.external_emission_factor:
          return item.purchasedGood.overrideEmissionFactors;
        default:
          return undefined;
      }
    },
    []
  );

  const setIngredients = useCallback(
    (modelledAs: PurchasedGoodModelledAsEnum) => {
      switch (modelledAs) {
        case PurchasedGoodModelledAsEnum.modelled_food:
          return item.purchasedGood.modelledFood?.ingredients.map(
            (ingredient) => ({
              entityUuid: ingredient.baseFood.uuid,
              name: ingredient.baseFood.name,
              entityType: 'base_food',
              quantity: ingredient.quantity,
              unitUuid: ingredient.unit.uuid,
              availableUnitTypes: [],
            })
          );
        default:
          return [emptyBaseFoodRow];
      }
    },
    [item.purchasedGood.modelledFood?.ingredients]
  );

  useEffect(() => {
    const stages: Array<StageEnum> = [...Object.keys(StageEnum)] as StageEnum[];

    const emissionFactors =
      item.modelledAs === PurchasedGoodModelledAsEnum.basic_food &&
      getEmissionFactors(
        stages,
        item.purchasedGood.basicFood.emissionFactors,
        item.purchasedGood.overrideEmissionFactors
      );

    if (item) {
      reset({
        remoteId: item.remoteId || undefined,
        name: item.purchasedGood.name,
        inputUnit: item.inputUnit || undefined,
        basicFoodUuid: item.purchasedGood.basicFood?.uuid || undefined,
        modelledAs: item.modelledAs,
        unit: item.itemUnit?.uuid,
        itemsPerPack: item.itemsPerPack || undefined,
        itemWeight: item.itemMassOrVolume || undefined,
        recipeDescription: item.purchasedGood.name,
        emissionFactors: setEmissionFactors(
          item.modelledAs,
          item,
          emissionFactors
        ),
        ingredients: setIngredients(item.modelledAs),
      });
    }
  }, [item, reset, setIngredients, setEmissionFactors]);

  const onSubmit = async (data: z.infer<typeof purchasedItemFormSchema>) => {
    setIsSubmitting(true);

    const baseUpdateData = {
      remoteId: data.remoteId,
      name: data.name,

      itemUnitUuid: data.unit,
      itemsPerPack: data.itemsPerPack,
      itemMassOrVolume: data.itemWeight,

      modelledAs: data.modelledAs,
    };
    const basicFoodUpdateData = data.modelledAs ===
      PurchasedGoodModelledAsEnum.basic_food && {
      ...baseUpdateData,
      basicFoodUuid: data.basicFoodUuid,
      overrideEmissionFactors: Object.keys(data.emissionFactors!).reduce(
        (acc, stage) => {
          const factor = data.emissionFactors![stage as StageEnum];
          if (factor?.method === 'custom') {
            acc[stage as StageEnum] = {
              value: factor?.value,
              source: factor?.source,
            };
          }
          return acc;
        },
        { ...emptyEmissionFactors } as Record<
          StageEnum,
          { value: number | null; source: string | null }
        >
      ),
    };
    const modelledFoodUpdate = data.modelledAs ===
      PurchasedGoodModelledAsEnum.modelled_food && {
      ...baseUpdateData,
      modelledFood: {
        ingredients: data.ingredients?.map((ingredient) => ({
          baseFoodUuid: ingredient.entityUuid,
          quantity: ingredient.quantity,
          unitUuid: ingredient.unitUuid,
        })),
      },
    };

    const LCAUpdate = data.modelledAs ===
      PurchasedGoodModelledAsEnum.external_emission_factor && {
      ...baseUpdateData,
      overrideEmissionFactors: Object.keys(data.emissionFactors!).reduce(
        (acc, stage) => {
          const factor = data.emissionFactors![stage as StageEnum];
          acc[stage as StageEnum] = {
            value: factor?.value,
            source: factor?.source || null,
          };
          return acc;
        },
        {} as Record<StageEnum, { value: number | null; source: string | null }>
      ),
    };

    const updateData =
      data.modelledAs === PurchasedGoodModelledAsEnum.basic_food
        ? basicFoodUpdateData
        : data.modelledAs === PurchasedGoodModelledAsEnum.modelled_food
          ? modelledFoodUpdate
          : LCAUpdate;

    try {
      await updateFoodActivity({
        activityUuid: item.uuid,
        data: updateData,
      });
      await refetchItem();
      reset();
      toast({
        title: 'Item updated',
        variant: 'success',
      });
      // Land on the overview so the effect of the edit is immediately visible.
      navigate(
        generatePath(purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK_ITEM, {
          item_uuid: item.uuid,
        }) + scopeSearch
      );
    } catch (error) {
      sentry.log(error);
      toast({
        title: 'Failed to update item',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteFoodActivity({ activityUuid: item.uuid });
      toast({
        title: 'Item deleted',
        variant: 'success',
      });
      closeDeleteModal();
      referrer
        ? navigate(referrer)
        : navigate(generatePath(purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK));
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.type === 'purchased-good-linked-to-sold-good'
      ) {
        toast({
          title: 'Cannot delete this item',
          description:
            'This purchased good is linked to one or more sold goods. Remove the link before deleting.',
          variant: 'destructive',
        });
      } else {
        sentry.log(error);
      }
    }
  };

  const modelledAs = watch('modelledAs');

  const hasPackConfiguration = !!item.itemUnit;

  return (
    <Form {...form}>
      <form
        className="min-h-[360px] max-w-3xl space-y-4"
        onSubmit={handleSubmit(onSubmit)}
      >
        <fieldset disabled={isUpdatingFoodActivity}>
          <div className="space-y-4">
            <div className="grid grid-cols-12 gap-2">
              <InputFormField
                name="remoteId"
                disabled
                label="Your ID"
                className="col-span-2"
              />
              <InputFormField
                name="name"
                disabled
                label="Food name"
                className="col-span-7"
              />
              {hasPackConfiguration && (
                <InputFormField
                  name="inputUnit"
                  options={unitOptions!}
                  className="col-span-3"
                  label="Original input unit"
                  disabled
                />
              )}
            </div>
            {hasPackConfiguration && (
              <div className="space-y-3 pt-1">
                <Heading variant="subsection">Pack weight</Heading>
                <Text variant="subtle">
                  For purchases where the unit is a non-standard unit, such as
                  each or packs, the purchased weight for this item is
                  calculated using a pack weight, which is derived from the
                  fields below.
                </Text>
                <div className="grid grid-cols-12 gap-3">
                  <InputFormField
                    name="itemsPerPack"
                    label="Items per pack"
                    className="col-span-3"
                  />
                  <InputFormField
                    name="itemWeight"
                    label="Item weight"
                    className="col-span-3"
                  />
                  <SelectFormField
                    name="unit"
                    options={unitOptions!}
                    label="Unit"
                    className="col-span-2"
                    useShortLabel
                    loadingOptions={unitsIsLoading}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="mt-8 space-y-4">
            <Heading variant="section">Modelling emissions</Heading>
            <div className="grid grid-cols-12 gap-2">
              <SelectFormField
                name="modelledAs"
                label="Modelled by"
                className="col-span-4"
                options={purchasedFoodModellingOptions}
                showDescription
                showIcon
                includeErrorMessage
                handleSelect={(value) => {
                  if (
                    value ===
                      PurchasedGoodModelledAsEnum.external_emission_factor &&
                    item.modelledAs !==
                      PurchasedGoodModelledAsEnum.external_emission_factor
                  ) {
                    form.setValue('emissionFactors', emptyEmissionFactors);
                    return;
                  }

                  form.resetField('emissionFactors');
                }}
              />
              {modelledAs === 'basic_food' && (
                <IngredientPicker
                  name="basicFoodUuid"
                  include={['base_foods']}
                  label="Matched food"
                  className="col-span-5"
                  showLabel
                  knownSelectedItem={
                    item.purchasedGood.basicFood
                      ? {
                          uuid: item.purchasedGood.basicFood.uuid,
                          name: item.purchasedGood.basicFood.name,
                          type: 'base_food',
                          availableUnitTypes: [],
                        }
                      : undefined
                  }
                />
              )}
            </div>

            {modelledAs === PurchasedGoodModelledAsEnum.basic_food &&
              item.modelledAs === PurchasedGoodModelledAsEnum.basic_food && (
                <div className="space-y-3 pt-1">
                  <Heading variant="subsection">Emission factors</Heading>
                  <Text variant="subtle">
                    Basic foods have four emissions factors, covering the
                    life-cycle stages from the farm up to your supplier. As
                    standard, we select the most up-to-date factors available
                    for your location. You can override the factors with values
                    from your supplier or another source. Please read our{' '}
                    <TextLink onClick={() => showIntercomArticle('customEFs')}>
                      guidelines for custom factors
                    </TextLink>{' '}
                    before doing so.
                  </Text>
                  <LifeCycleStageModelling
                    baseEmissionFactors={
                      item.purchasedGood.basicFood!.emissionFactors!
                    }
                    allowCustomValues
                    showCustomSelector
                  />
                </div>
              )}

            {modelledAs === PurchasedGoodModelledAsEnum.modelled_food && (
              <div className="mb-6 space-y-3 pt-1">
                <div className="flex items-center gap-4">
                  {item.purchasedGood.modelledFood && (
                    <div className="flex flex-col items-center">
                      <RatingCloud rating={item.emissionsCategory} />
                      <Text>{formatSmallNumber(item.emissionFactor)}</Text>
                      <Unit variant="kgC02e/kg" />
                    </div>
                  )}

                  {item.purchasedGood.modelledFood ? (
                    <Text>
                      The emissions for this item are calculated based on the
                      carbon intensity of the recipe below. A carbon intensity
                      of {formatSmallNumber(item.emissionFactor)} means that for
                      every 1 kg produced,{' '}
                      {formatSmallNumber(item.emissionFactor)} kg of carbon
                      dioxide equivalent is emitted.
                    </Text>
                  ) : (
                    <Text>
                      The emissions for modelled foods are determined by their
                      ingredients. You can either input a recipe below or use
                      our tool to generate one from the description.
                    </Text>
                  )}
                </div>
                <ModelledFood
                  item={item}
                  modelledFood={item.purchasedGood.modelledFood}
                  isSubmitting={isSubmitting}
                />
              </div>
            )}

            {modelledAs ===
              PurchasedGoodModelledAsEnum.external_emission_factor && (
              <div className="space-y-3 pt-1">
                <Heading variant="subsection">Emission factors</Heading>
                <Text variant="subtle">
                  If you have completed a full product assessment with an
                  external provider or have the values from your supplier you
                  can enter them below. Please read our{' '}
                  <TextLink onClick={() => showIntercomArticle('customEFs')}>
                    guidelines for custom factors
                  </TextLink>{' '}
                  before doing so.
                </Text>
                <LifeCycleStageModelling
                  baseEmissionFactors={formatEmissionFactors(
                    item.emissionsFactors
                  )}
                  allowCustomValues
                  showSourceColumn
                />
              </div>
            )}
          </div>
          <div className="mt-12 space-y-4">
            <div className="flex justify-between ">
              <div className="col-span-4">
                <Button
                  type="submit"
                  disabled={isUpdatingFoodActivity}
                  loading={isUpdatingFoodActivity}
                >
                  Update purchased item
                </Button>
              </div>
              <div className="col-span-4">
                <Button
                  type="button"
                  variant="destructive"
                  onClick={showDeleteModal}
                  disabled={isDeletingFoodActivity || isUpdatingFoodActivity}
                >
                  Delete item
                </Button>
                {deleteModal({
                  header: 'Delete purchased item',
                  content: (
                    <Text>
                      Deleting this item will also remove all related{' '}
                      <TextLink
                        to={generatePath(
                          purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK_ITEM_PURCHASES,
                          {
                            item_uuid: item.uuid,
                          }
                        )}
                      >
                        purchases
                      </TextLink>{' '}
                      from your reports. This cannot be undone.
                    </Text>
                  ),
                  footer: (
                    <div className="space-x-2">
                      <Button
                        variant="outline"
                        onClick={closeDeleteModal}
                        disabled={isDeletingFoodActivity}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={handleDelete}
                        loading={isDeletingFoodActivity}
                      >
                        Delete item
                      </Button>
                    </div>
                  ),
                })}
              </div>
            </div>
            <Text variant="subtle">
              Updating the modelling for this item will update all related{' '}
              <TextLink
                to={generatePath(
                  purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK_ITEM_PURCHASES,
                  {
                    item_uuid: item.uuid,
                  }
                )}
              >
                purchases
              </TextLink>
              , all reports will need to be republished.
            </Text>
          </div>
        </fieldset>
      </form>
    </Form>
  );
}
